import {
  Button,
  Drawer,
  Empty,
  Label,
  Loading,
  Message,
  Title,
} from "@components";
import { If } from "@components/control-statements";
import { useEffect, useMemo, useState } from "react";

import { FlowList } from "@containers/flows/FlowList";
import { FlowAPI } from "@providers/FlowAPI";
import { useNavigate } from "react-router-dom";
import { OlgaEnvironmentsAPI } from "../../providers/OlgaEnvironmentsAPI";
import dayjs from "dayjs";
import { BoxTypes } from "shared/containers/boxesOraculo/BoxTypes";
import styles from "@styles/pages/flow/FlowList.module.scss";
import { UserStorage } from "shared/tools/UserStorage";

export default function FlowFolder() {
  const [folder, setFolder] = useState({});
  const [flows, setFlows] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [flowsData, setFlowsData] = useState([]);
  const [openPublish, setOpenPublish] = useState(false);

  const path = window.location.pathname;
  const parts = path.split("/");
  const folderId = parts[3];

  useEffect(() => {
    if (folderId) {
      loadFolder();
      loadFlows();
    }
  }, [folderId]);

  const loadFlows = async () => {
    const myFlowsResponse = await FlowAPI.getFlows(folderId);
    setFlows(myFlowsResponse.getData([]));
    setPageLoading(false);
  };

  useEffect(() => {
    const load = async () => {
      if (flows.length === 0) return;

      const entries = await Promise.all(
        flows.map(async (flow) => {
          const stepsResponse = await FlowAPI.getStepsByFlowId(flow.id);

          return [
            flow.id,
            {
              ...flow,
              steps: stepsResponse.getData({ boxes: [], links: [] }),
            },
          ];
        }),
      );

      setFlowsData(Object.fromEntries(entries));
    };

    load();
  }, [flows]);

  const loadFolder = async () => {
    setPageLoading(true);

    const environmentConfigurations =
      await OlgaEnvironmentsAPI.getConfigurations();
    const folders = environmentConfigurations.getData({})?.flow_folders || [];
    const folder = folders.find((folder) => folder.id === folderId);
    setFolder(folder || {});
  };

  const navigate = useNavigate();

  const getCreatives = (steps) => {
    const creativeActionTypes = [
      "PushCreativeAction",
      "SmsCreativeAction",
      "EmailCreativeAction",
    ];

    if (!steps?.boxes) return [];

    const groupCreatives = steps.boxes
      .filter((b) => b.type === "CreativeGroupAction")
      .flatMap(
        (b) =>
          b.data?.creatives
            ?.filter((c) => creativeActionTypes.includes(c.type))
            .map((c) => ({ ...c.data, type: c.type })) || [],
      );

    const directCreatives = steps.boxes
      .filter((b) => creativeActionTypes.includes(b.type))
      .map((b) => ({ ...b.data, type: b.type }));

    return [...groupCreatives, ...directCreatives];
  };

  const getCreativesWithMessage = (steps) => {
    const creativesWithMessage = getCreatives(steps)
      .flat(Infinity)
      .map((a) => {
        const message = a?.creative?.message;
        const hasMessage = message?.template || message?.filename || null;

        return hasMessage;
      })
      .filter((b) => b !== null);

    return creativesWithMessage;
  };

  const getAtLastOneAudience = (steps) => {
    const audienceTypes = [
      "FullAudience",
      "SavedAudience",
      "ImportedAudience",
      "EventSource",
      "BroadcastAudience",
    ];
    const boxes = steps?.boxes;

    const isValid = (type, validationKey) => {
      const filteredAudiences = boxes?.filter((a) => a.type === type);
      return filteredAudiences?.every((s) => s.data?.[validationKey]);
    };

    const isValidfullAudiences = isValid("ImportedAudience", "type");
    const isValidSavedAudiences = isValid("SavedAudience", "audienceId");
    const isValidImportedAudiences = isValid("ImportedAudience", "fileName");
    const isValidEventSources = isValid("EventSource", "eventType");

    return (
      isValidSavedAudiences &&
      isValidImportedAudiences &&
      isValidEventSources &&
      isValidfullAudiences &&
      steps?.boxes?.some((a) => audienceTypes.includes(a.type))
    );
  };

  const getBlockPublish = (steps) => {
    const creatives = getCreatives(steps).map((c) => c.creative);

    const pointFlowToday = steps?.boxes
      .filter((a) => a.type === "SpecificDayConditional")
      .find((a) => a?.data?.use_type === "TODAY");

    const differenceInHours = creatives.map((creative) => {
      if (!creative) {
        return false;
      }

      if (creative.type === "PushCreativeAction") return 99;

      const c = creative?.sendTime;
      const now = dayjs();
      const hour = dayjs().hour(c.slice(0, 2));
      const differenceInHours = hour.diff(now, "hour");

      return differenceInHours;
    });

    const differenceGreaterThanTwoHours = differenceInHours.some((d) => d < 2);
    const blockPublish = !!differenceGreaterThanTwoHours && !!pointFlowToday;

    return blockPublish;
  };

  const validateFlow = (flowData) => {
    const blockPublish = getBlockPublish(flowData.steps);
    const atLastOneAudience = getAtLastOneAudience(flowData.steps);
    const allCreativesHaveMessage = getCreativesWithMessage(flowData.steps);
    const creativesQuantity = getCreatives(flowData.steps)?.flat(Infinity);

    const boxes = flowData.steps?.boxes || [];

    const isBroadcastFlow = boxes.some(
      (box) => box.type === BoxTypes.BroadcastAudience,
    );
    const hasSpecificClimateConditional = boxes.some(
      (box) => box.type === BoxTypes.SpecificClimateConditional,
    );

    const isValidBroadcastFlow = isBroadcastFlow
      ? !hasSpecificClimateConditional
      : true;

    const isValidFlow =
      creativesQuantity?.length > 0 &&
      allCreativesHaveMessage &&
      atLastOneAudience &&
      isValidBroadcastFlow;

    const pointFlow = flowData.steps?.boxes?.some(
      (b) =>
        b.type === "SpecificDayConditional" && b?.data?.use_type === "TODAY",
    );

    return {
      id: flowData.id,
      name: flowData.name,
      blockPublish,
      pointFlow,
      isValidFlow,
      isInvalid: blockPublish || pointFlow || !isValidFlow,
    };
  };

  const invalidFlows = useMemo(() => {
    if (!flowsData || Object.keys(flowsData).length === 0) return [];

    return Object.values(flowsData)
      .map(validateFlow)
      .filter((f) => f.isInvalid);
  }, [flowsData]);

  const hasBlockPublish = useMemo(
    () => invalidFlows.some((f) => f.blockPublish),
    [invalidFlows],
  );

  const hasPointFlow = useMemo(
    () => invalidFlows.some((f) => f.pointFlow && !f.blockPublish),
    [invalidFlows],
  );

  const hasInvalidFlow = useMemo(
    () => invalidFlows.some((f) => !f.isValidFlow),
    [invalidFlows],
  );

  const validFlows = useMemo(() => {
    if (!flows?.length) return [];

    const invalidIds = new Set(invalidFlows.map((f) => f.id));

    return flows.filter((flow) => !invalidIds.has(flow.id));
  }, [flows, invalidFlows]);

  const executeForAllFlows = async (
    flowsToExecute,
    apiCall,
    successMessage,
    errorMessage,
  ) => {
    if (!flowsToExecute?.length) {
      alert({
        text: "Nenhum fluxo válido para executar.",
        buttonText: "Ok",
      });
      return;
    }

    try {
      setPageLoading(true);

      const errors = [];

      for (const flow of flowsToExecute) {
        const res = await apiCall(flow.id);
        errors.push(...res.getErrors([]));
      }

      setOpenPublish(false);

      if (errors.length > 0) {
        alert({
          text: errors[0] || errorMessage,
          buttonText: "Voltar",
        });
        return;
      }

      alert({
        text: successMessage,
        buttonText: "Ok",
      });

      loadFlows();
    } finally {
    }
  };

  const pauseAll = async () => {
    await executeForAllFlows(
      flows,
      FlowAPI.putPauseFlow,
      "Fluxos pausados com sucesso!",
      "Ocorreu um erro ao pausar",
    );
  };

  const publishAll = async () => {
    await executeForAllFlows(
      validFlows,
      FlowAPI.putPublishFlow,
      "Fluxos publicados com sucesso!",
      "Ocorreu um erro ao publicar",
    );
  };

  return (
    <>
      {pageLoading ? (
        <div className="d-flex flex-column w-100">
          <Loading fullContent />
        </div>
      ) : (
        <>
          <Title
            backTo={{ url: "/fluxos", name: "Fluxos" }}
            title={folder?.name || ""}
          >
            <If cond={flows?.length && UserStorage.isGodMode}>
              <Button onClick={pauseAll} className="mr-10">
                Pausar fluxos
              </Button>
              <Button design="primary" onClick={() => setOpenPublish(true)}>
                Publicar fluxos
              </Button>
            </If>
          </Title>

          <FlowList
            flows={flows}
            onFlowClick={(flow) => {
              navigate(`/fluxos/${flow.id}`);
            }}
            onLoadFlows={() => loadFlows()}
          />

          <If cond={flows.length == 0}>
            <Empty>
              <i className="far fa-arrows-split-up-and-left" />
              <p>Não há fluxos nesta pasta.</p>
            </Empty>
          </If>
        </>
      )}

      <Drawer
        title="Deseja publicar os fluxos?"
        direction="top-alert"
        visible={openPublish}
        onClose={() => setOpenPublish(false)}
      >
        <Label>
          Atenção: ao publicar os fluxos, todos os relatórios serão zerados.
        </Label>
        {hasPointFlow && !hasBlockPublish && (
          <>
            <Message
              type="danger"
              text="Atenção! Ao publicar este fluxo, ele não será mais editável, confirme todas as configurações."
            />

            <ul className={styles.campaigns}>
              {invalidFlows
                .filter((f) => f.pointFlow && !f.blockPublish)
                .map((f) => (
                  <li key={f.id}>
                    <b>
                      {f.id} - {f.name}
                    </b>
                  </li>
                ))}
            </ul>
          </>
        )}

        {hasBlockPublish && (
          <>
            <Message
              type="danger"
              text="Não é possível publicar os seguintes fluxos, pois o horário do disparo está configurado para menos de 2 horas do horário atual."
            />

            <ul className={styles.campaigns}>
              {invalidFlows
                .filter((f) => f.blockPublish)
                .map((f) => (
                  <li key={f.id}>
                    <b>
                      {f.id} - {f.name}
                    </b>
                  </li>
                ))}
            </ul>
          </>
        )}

        {hasInvalidFlow && (
          <>
            <Message
              type="danger"
              text="Não é possível publicar os seguintes fluxos, pois eles não foram devidamente configurados."
            />

            <ul className={styles.campaigns}>
              {invalidFlows
                .filter((f) => !f.isValidFlow)
                .map((f) => (
                  <li key={f.id}>
                    <b>
                      {f.id} - {f.name}
                    </b>
                  </li>
                ))}
            </ul>
          </>
        )}

        <div className="mt-20 d-flex align-center w-100">
          <Button
            className="w-100"
            onClick={() => setOpenPublish(false)}
            design="default-2"
          >
            Cancelar
          </Button>
          <Button
            className="w-100 ml-15"
            onClick={() => publishAll(true)}
            design="primary"
          >
            Sim, publicar
          </Button>
        </div>
      </Drawer>
    </>
  );
}
